import { type FC, useEffect, useState } from 'react';

import type { AddMeasureBody, MeasureVm } from '@eluve/api-contract';
import {
  allRowsConfirmed,
  measureReviewPosture,
  needsVerdict,
} from '@eluve/api-contract';
import {
  Button,
  Icon,
  Input,
  Label,
  Stack,
  Tag,
  Text,
  cn,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

import type { CarePlanEvidenceSelectionHandler } from './CarePlanEvidenceDrawer';
import { ItemEditForm } from './ItemEditForm';
import { InlineChangeDiff } from './PendingChangeDiff';
import { SectionBulkControls } from './SectionBulkControls';
import { TrendSparkline } from './TrendSparkline';

type MeasureEditFields = Omit<AddMeasureBody, 'appointmentId'>;

/** The target's value part ("≥ 80°", "50–70 kg") without the "Target" word —
 *  the caller renders it through the `carePlans.measures.targetValue` key. */
const targetLabel = (m: MeasureVm): string | null => {
  const t = m.target;
  if (!t) return null;
  if (t.value !== null)
    return `${t.comparator ?? ''}${t.comparator ? ' ' : ''}${t.value}${t.unit ? ` ${t.unit}` : ''}`;
  if (t.low !== null || t.high !== null)
    return `${t.low ?? ''}–${t.high ?? ''}${t.unit ? ` ${t.unit}` : ''}`;
  return null;
};

/** Parse a target field ("50°", "3/10", "≥ 80°") into a value + unit; the
 *  comparator is set by the direction toggle, so a leading ≥/≤ is ignored. */
const parseTarget = (
  text: string,
): { value: number | null; unit: string | null } => {
  const match = text.trim().match(/(-?\d+(?:\.\d+)?)\s*(.*)$/);
  if (!match) return { value: null, unit: text.trim() || null };
  return { value: Number(match[1]), unit: match[2]?.trim() || null };
};

const initialTargetText = (m: MeasureVm): string => {
  const t = m.target;
  if (!t || t.value === null) return '';
  return `${t.value}${t.unit ? ` ${t.unit}` : ''}`;
};

const initialDirection = (m: MeasureVm): 'higher' | 'lower' =>
  m.target?.comparator === '<=' || m.target?.comparator === '<'
    ? 'lower'
    : 'higher';

const measureRowKey = (measure: MeasureVm): string =>
  measure.thisVisit?.observationId ?? `target:${measure.key}`;

const MeasureEditForm: FC<{
  initial: { name: string; targetText: string; direction: 'higher' | 'lower' };
  disabled?: boolean;
  onCancel: () => void;
  onSave: (fields: MeasureEditFields) => void;
  onDelete?: () => void;
}> = ({ initial, disabled, onCancel, onSave, onDelete }) => {
  const { t } = useTranslation();
  const [name, setName] = useState(initial.name);
  const [targetText, setTargetText] = useState(initial.targetText);
  const [direction, setDirection] = useState(initial.direction);
  const isEdit = Boolean(onDelete);

  const dirButton = (
    value: 'higher' | 'lower',
    arrow: string,
    label: string,
  ) => (
    <Button
      size="sm"
      variant={direction === value ? 'default' : 'outline'}
      disabled={disabled}
      aria-label={label}
      aria-pressed={direction === value}
      onClick={() => setDirection(value)}
      className="w-9"
    >
      {arrow}
    </Button>
  );

  return (
    <ItemEditForm
      title={
        isEdit
          ? t('carePlans.measures.editTitle')
          : t('carePlans.measures.addTitle')
      }
      saveLabel={
        isEdit ? t('carePlans.review.saveChanges') : t('carePlans.review.save')
      }
      saveDisabled={name.trim().length === 0}
      disabled={disabled}
      onCancel={onCancel}
      onSave={() => {
        const { value, unit } = parseTarget(targetText);
        onSave({
          name: name.trim(),
          targetValue: value,
          targetUnit: unit,
          direction,
        });
      }}
      onDelete={onDelete}
      deleteLabel={t('carePlans.measures.deleteMeasure')}
      deleteConfirmTitle={t('carePlans.measures.deleteConfirmTitle')}
      deleteConfirmBody={t('carePlans.measures.deleteConfirmBody')}
      className="border-border-primary bg-bg-primary shadow-sm"
    >
      <Stack direction="vertical" gap={3} className="mt-3">
        <Input
          value={name}
          placeholder={t('carePlans.measures.namePlaceholder')}
          onChange={(e) => setName(e.target.value)}
          aria-label={t('carePlans.measures.namePlaceholder')}
        />
        <Stack direction="horizontal" align="end" gap={4}>
          <Stack direction="vertical" gap={1} fullWidth={false}>
            <Text size="xs" color="secondary">
              {t('carePlans.measures.target')}
            </Text>
            <Input
              value={targetText}
              placeholder={t('carePlans.measures.targetPlaceholder')}
              onChange={(e) => setTargetText(e.target.value)}
              className="w-28"
              aria-label={t('carePlans.measures.targetAriaLabel')}
            />
          </Stack>
          <Stack direction="vertical" gap={1} fullWidth={false}>
            <Label className="text-xs font-normal text-text-secondary">
              {t('carePlans.measures.improves')}
            </Label>
            <Stack direction="horizontal" align="center" gap={2}>
              {dirButton('higher', '↑', t('carePlans.measures.higherIsBetter'))}
              {dirButton('lower', '↓', t('carePlans.measures.lowerIsBetter'))}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </ItemEditForm>
  );
};

/** "previous → [input] Δ" — the last visit's value ahead of the live field and
 *  the signed delta after it, green when moving the measure's better way. */
const MeasureDelta: FC<{
  trend: MeasureVm['trend'];
  lowerIsBetter: boolean;
}> = ({ trend, lowerIsBetter }) => {
  if (trend.prev === null || trend.current === null || trend.delta === null) {
    return null;
  }
  const better = lowerIsBetter ? trend.delta < 0 : trend.delta > 0;
  return (
    <Text
      size="xs"
      weight="medium"
      className={cn(
        'shrink-0 tabular-nums',
        trend.delta === 0
          ? 'text-text-secondary'
          : better
            ? 'text-text-positive'
            : 'text-text-negative',
      )}
    >
      {trend.delta === 0
        ? '±0'
        : `${trend.delta > 0 ? '↑' : '↓'}${Math.abs(trend.delta)}`}
    </Text>
  );
};

const MeasureValueInput: FC<{
  capture: NonNullable<MeasureVm['thisVisit']>;
  label: string;
  disabled?: boolean;
  onCommit: (value: number) => void;
}> = ({ capture, label, disabled, onCommit }) => {
  const { t } = useTranslation();
  const initial = capture.value !== null ? String(capture.value) : '';
  const [draft, setDraft] = useState(initial);
  useEffect(() => setDraft(initial), [initial]);
  const missing = capture.value === null && !capture.stringValue;
  const aiFilled =
    capture.confidence !== null && capture.decision === 'pending';
  const readOnly = capture.stringValue !== null;

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed === initial.trim()) return;
    const parsed = Number(trimmed);
    if (trimmed === '' || Number.isNaN(parsed)) return;
    onCommit(parsed);
  };

  if (readOnly) {
    const value = capture.stringValue ?? (initial || '—');

    return (
      <Stack
        direction="horizontal"
        align="center"
        className="h-8 min-w-0 rounded-md border border-input bg-bg-secondary px-2.5 md:w-56"
      >
        <Text size="sm" className="truncate">
          {value}
        </Text>
      </Stack>
    );
  }

  return (
    <div className="relative w-16 shrink-0">
      <Input
        type="number"
        value={draft}
        placeholder="—"
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
        className={cn(
          'h-8 tabular-nums',
          missing && 'border-border-warning-subtle bg-bg-warning-subtle',
        )}
        aria-label={t('carePlans.measures.thisVisitAriaLabel', { label })}
      />
      {aiFilled && !missing && (
        <span className="absolute -top-1 -right-1 flex size-3 items-center justify-center rounded-full bg-bg-insights-strong-primary text-[8px] text-text-insights-strong">
          +
        </span>
      )}
    </div>
  );
};

export const MeasuresSection: FC<{
  measures: MeasureVm[];
  disabled?: boolean;
  showBulkUndo?: boolean;
  onConfirm: (m: MeasureVm) => void;
  onConfirmAll?: (measures: MeasureVm[]) => void;
  onUndoConfirmAll?: () => void;
  onReject: (m: MeasureVm) => void;
  onUndo: (m: MeasureVm) => void;
  onEditValue: (m: MeasureVm, value: number) => void;
  onEditMeasure: (m: MeasureVm, fields: MeasureEditFields) => void;
  onDelete: (m: MeasureVm) => void;
  onAddMeasure: (fields: MeasureEditFields) => void;
  onSelectEvidence?: CarePlanEvidenceSelectionHandler;
}> = ({
  measures,
  disabled,
  showBulkUndo,
  onConfirm,
  onConfirmAll,
  onUndoConfirmAll,
  onReject,
  onUndo,
  onEditValue,
  onEditMeasure,
  onDelete,
  onAddMeasure,
  onSelectEvidence,
}) => {
  const { t } = useTranslation();
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const confirmable = measures.filter((measure) =>
    needsVerdict(measureReviewPosture(measure)),
  );
  const allConfirmed = allRowsConfirmed(measures.map(measureReviewPosture));

  return (
    <section className="w-full rounded-xl border border-border-secondary bg-bg-primary p-4 shadow-sm">
      <SectionBulkControls
        title={t('carePlans.measures.title')}
        confirmableCount={confirmable.length}
        allConfirmed={allConfirmed}
        showUndoAll={Boolean(showBulkUndo)}
        disabled={disabled}
        onConfirmAll={() =>
          onConfirmAll
            ? onConfirmAll(confirmable)
            : confirmable.forEach(onConfirm)
        }
        onUndoAll={onUndoConfirmAll}
      />

      <ul className="mt-3 divide-y divide-border-secondary border-t border-border-secondary pt-1">
        {measures.map((measure) => {
          const rowKey = measureRowKey(measure);
          const capture = measure.thisVisit;
          const sourceEvidence = capture?.sourceEvidence ?? null;
          const missing =
            !capture || (capture.value === null && !capture.stringValue);
          const posture = measureReviewPosture(measure);
          const targetChange =
            measure.targetChange?.decision === 'pending'
              ? measure.targetChange
              : null;

          return (
            <li key={rowKey} className="py-2">
              {editingKey === rowKey ? (
                <MeasureEditForm
                  initial={{
                    name: measure.label,
                    targetText: initialTargetText(measure),
                    direction: initialDirection(measure),
                  }}
                  disabled={disabled}
                  onCancel={() => setEditingKey(null)}
                  onSave={(fields) => {
                    onEditMeasure(measure, fields);
                    setEditingKey(null);
                  }}
                  onDelete={() => {
                    onDelete(measure);
                    setEditingKey(null);
                  }}
                />
              ) : (
                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 md:grid-cols-[minmax(12rem,1fr)_auto_auto_auto]">
                  {sourceEvidence && onSelectEvidence ? (
                    <div
                      role="button"
                      tabIndex={0}
                      aria-label={`Open evidence for ${measure.label}`}
                      className="min-w-0 cursor-pointer rounded-md text-left outline-none focus-visible:ring-3 focus-visible:ring-ring/50 md:pr-3"
                      onClick={(event) =>
                        onSelectEvidence(
                          {
                            label: measure.label,
                            itemType: 'measure',
                            evidence: sourceEvidence,
                          },
                          event.currentTarget,
                        )
                      }
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onSelectEvidence(
                            {
                              label: measure.label,
                              itemType: 'measure',
                              evidence: sourceEvidence,
                            },
                            event.currentTarget,
                          );
                        }
                      }}
                    >
                      <Stack direction="vertical" gap={0}>
                        <Text size="sm" weight="medium">
                          {measure.label}
                        </Text>
                        {targetChange ? (
                          <span className="text-xs">
                            <Text inline size="xs" color="secondary">
                              {t('carePlans.measures.target')}{' '}
                            </Text>
                            <InlineChangeDiff
                              previous={
                                targetChange.previous ??
                                t('carePlans.review.notSet')
                              }
                              proposed={targetChange.proposed}
                            />
                          </span>
                        ) : (
                          targetLabel(measure) && (
                            <Text size="xs" color="secondary">
                              {t('carePlans.measures.targetValue', {
                                target: targetLabel(measure) ?? '',
                              })}
                            </Text>
                          )
                        )}
                      </Stack>
                    </div>
                  ) : (
                    <Stack
                      direction="vertical"
                      gap={0}
                      className="min-w-0 md:pr-3"
                    >
                      <Text size="sm" weight="medium">
                        {measure.label}
                      </Text>
                      {targetChange ? (
                        <span className="text-xs">
                          <Text inline size="xs" color="secondary">
                            {t('carePlans.measures.target')}{' '}
                          </Text>
                          <InlineChangeDiff
                            previous={
                              targetChange.previous ??
                              t('carePlans.review.notSet')
                            }
                            proposed={targetChange.proposed}
                          />
                        </span>
                      ) : (
                        targetLabel(measure) && (
                          <Text size="xs" color="secondary">
                            {t('carePlans.measures.targetValue', {
                              target: targetLabel(measure) ?? '',
                            })}
                          </Text>
                        )
                      )}
                    </Stack>
                  )}
                  <Stack
                    direction="horizontal"
                    align="center"
                    gap={2}
                    fullWidth={false}
                    className="hidden shrink-0 md:flex"
                  >
                    <TrendSparkline trend={measure.trend} />
                    {measure.trend.prev !== null && (
                      <Text
                        size="xs"
                        color="secondary"
                        className="tabular-nums"
                      >
                        {measure.trend.prev} →
                      </Text>
                    )}
                    <MeasureDelta
                      trend={measure.trend}
                      lowerIsBetter={initialDirection(measure) === 'lower'}
                    />
                  </Stack>
                  <Stack
                    direction="horizontal"
                    align="center"
                    justify="end"
                    gap={2}
                    fullWidth={false}
                    className="min-w-0 justify-self-end"
                  >
                    {capture?.unit && !missing && (
                      <Text size="xs" color="secondary" className="shrink-0">
                        {capture.unit}
                      </Text>
                    )}
                    {capture && (
                      <MeasureValueInput
                        capture={capture}
                        label={measure.label}
                        disabled={disabled}
                        onCommit={(value) => onEditValue(measure, value)}
                      />
                    )}
                  </Stack>
                  <Stack
                    direction="horizontal"
                    align="center"
                    justify="end"
                    gap={1}
                    fullWidth={false}
                    className="col-span-2 shrink-0 justify-self-end md:col-span-1"
                  >
                    {sourceEvidence && onSelectEvidence && (
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={`View source for ${measure.label}`}
                        onClick={(event) =>
                          onSelectEvidence(
                            {
                              label: measure.label,
                              itemType: 'measure',
                              evidence: sourceEvidence,
                            },
                            event.currentTarget,
                          )
                        }
                      >
                        <Icon name="FileText" size="auto" />
                      </Button>
                    )}
                    {posture.reviewable && posture.decision !== 'pending' && (
                      <Tag
                        size="s"
                        subtle={false}
                        color={
                          posture.decision === 'rejected'
                            ? 'negative'
                            : 'positive'
                        }
                        label={
                          posture.decision === 'rejected'
                            ? t('carePlans.review.rejected')
                            : t('carePlans.review.confirmed')
                        }
                        className="h-[30px] rounded-lg px-3"
                      />
                    )}
                    {posture.reviewable && posture.decision === 'pending' && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={disabled}
                        className="border-border-positive-subtle bg-bg-positive-subtle text-text-positive hover:bg-bg-positive-subtle"
                        onClick={() => onConfirm(measure)}
                      >
                        <Icon name="Check" size="auto" />
                        {t('common.confirm')}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={disabled}
                      onClick={() => setEditingKey(rowKey)}
                      aria-label={t('carePlans.review.edit')}
                    >
                      <Icon name="Pencil" size="auto" />
                    </Button>
                    {posture.reviewable && posture.decision === 'pending' && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={disabled}
                        className="border-border-negative-subtle bg-bg-negative-subtle text-text-negative hover:bg-bg-negative-subtle"
                        onClick={() => onReject(measure)}
                        aria-label={t('carePlans.review.reject')}
                      >
                        <Icon name="X" size="auto" />
                      </Button>
                    )}
                    {posture.reviewable && posture.decision !== 'pending' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={disabled}
                        onClick={() => onUndo(measure)}
                      >
                        <Icon name="Undo2" size="auto" />
                        {t('carePlans.review.undo')}
                      </Button>
                    )}
                  </Stack>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {adding ? (
        <div className="mt-3">
          <MeasureEditForm
            initial={{ name: '', targetText: '', direction: 'higher' }}
            disabled={disabled}
            onCancel={() => setAdding(false)}
            onSave={(fields) => {
              onAddMeasure(fields);
              setAdding(false);
            }}
          />
        </div>
      ) : (
        <Button
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={() => setAdding(true)}
          className="mt-3"
        >
          <Icon name="Plus" size="auto" />
          {t('carePlans.measures.addMeasure')}
        </Button>
      )}
    </section>
  );
};
